import { Grid, makeStyles, Box } from '@material-ui/core';
import { Content, Link, Page } from '@backstage/core-components';
// The widgets themselves rather than their card-extension wrappers: those
// exist only so the grid can lay them out and show them in its add-widget
// picker, neither of which applies to a fixed arrangement.
import { PullRequestsCard } from './PullRequestsCard';
import { PullRequestListsProvider } from './pullRequestLists';
import { HomeSourcesProvider } from './homeSources';
import { NeedsAttention } from './NeedsAttention';
import { MyWorkspace } from './MyWorkspace';
import { TeamNotSet } from './TeamNotSet';
import { RecentlyUsed } from './RecentlyUsed';
import { ServicesYouOwn } from './ServicesYouOwn';
import { CiHealthOverview } from './CiHealthOverview';
import { RecentDeployments } from './RecentDeployments';
import { CI_WIDGET_ANCHOR_ID, PR_WIDGET_ANCHOR_ID } from './anchors';
import { CockpitWelcome } from './CockpitWelcome';
import { cockpitTokens } from './cockpitTokens';

const useStyles = makeStyles(theme => ({
  /* Die Einstiegsleiste am Fuss: Links, keine Karten. Rahmen, Radius und
     Schatten sagen jeweils „eigenstaendiges Objekt“ — hier ist nichts
     eigenstaendig, deshalb traegt sie keins davon. */
  entryStrip: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'baseline',
    gap: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${cockpitTokens.color.border}`,
  },
  entryStripLabel: {
    fontSize: '0.8rem',
    color: cockpitTokens.color.textFaint,
  },
  entryStripLink: {
    fontSize: '0.875rem',
    color: cockpitTokens.color.brand,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  /*
    The top of the page (DX-546): the attention card with My workspace beside
    it; then the pull-request card and Recently used, each across the full
    width. The pull-request card sits under the queue because the queue's
    review rows lead into it. It first stood in the queue's column with the
    workspace beside both, and the workspace — four figures — ended long before
    the list did, leaving an empty column the height of the list. Across the
    width, a long pull-request title also stays on one line.

    On a screen too narrow for two columns it is one column, and the workspace
    comes before the pull requests — the figures before the lists.
  */
  top: {
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateAreas: '"queue" "side" "reviews" "recent"',
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
      gridTemplateAreas: '"queue side" "reviews reviews" "recent recent"',
    },
  },
  queue: { gridArea: 'queue', minWidth: 0 },
  reviews: { gridArea: 'reviews', minWidth: 0, alignSelf: 'start' },
  recent: { gridArea: 'recent', minWidth: 0, alignSelf: 'start' },
  side: {
    gridArea: 'side',
    alignSelf: 'start',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(3),
  },
  /* My workspace as a card of the same make as the attention card beside it,
     its title in a head of the same height. The title stood above the figures
     at first, and pushed them a line below the card they sit beside. */
  workspace: {
    background: cockpitTokens.color.surface,
    border: `1px solid ${cockpitTokens.color.border}`,
    borderRadius: cockpitTokens.radius.card,
    boxShadow: cockpitTokens.shadow.card,
    overflow: 'hidden',
  },
  workspaceHead: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
  },
  workspaceTitle: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: cockpitTokens.color.text,
  },
}));

// The four fields, still reachable from the page a reader lands on — as a
// line of links at the foot rather than the tiles that used to open it. The
// navigation carries the same four (DX-74); this is the reminder that they
// exist, not the way in.
const fieldLinks = [
  { to: '/platform', label: 'Platform' },
  { to: '/product', label: 'Product' },
  { to: '/dx', label: 'DX' },
  { to: '/consulting', label: 'Consulting' },
];

/**
 * The home page as an attention queue (DX-307).
 *
 * The page used to show state — a row of counts and the cards beneath it. It
 * now opens on open work, one action per row, beside four figures about the
 * colleague's workspace; then their pull requests; then what they opened last;
 * then the cards that hold the detail the rows lead into.
 *
 * Michael's draft ends below the recently used table, and the cards stay
 * under it rather than going: CI health is where a failing build's summary
 * row leads, and the pull-request card — where the reviews the queue counts
 * are listed (and what colleagues asked for in DX-391) — moved up under the
 * queue with DX-546. The sections that stood as tabs above them — Activity,
 * Starred, Teams — are gone. Activity waited for DX-83, which this page
 * replaces: a chronicle says what happened, a queue says what to do. What the
 * other two held is in the shell: the starred entities in the navigation and
 * the catalog's Starred tab, the responsibility map under its own entry, and
 * the pages visited lately in Recently used.
 *
 * Quick actions went with DX-546 too: every tile repeated a navigation entry
 * or the palette, and the one that did not, Deploy, could only say it was
 * unavailable. The palette (⌘K) is the way to everything they reached.
 */
export const HomePage = () => {
  const classes = useStyles();

  return (
    <Page themeId="home">
      <Content>
        {/* One answer per source for everything on the page that reads it —
            the pull-request lists, and the catalog, CI and deployments
            snapshots — so a figure in one place and a row in another cannot
            disagree (DX-391, DX-307). */}
        <HomeSourcesProvider>
          <PullRequestListsProvider>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CockpitWelcome />
              </Grid>

              <Grid item xs={12}>
                <div className={classes.top}>
                  <div className={classes.queue}>
                    <NeedsAttention />
                  </div>
                  <div className={classes.side}>
                    <section
                      className={classes.workspace}
                      aria-labelledby="my-workspace-title"
                    >
                      <div className={classes.workspaceHead}>
                        <h2
                          id="my-workspace-title"
                          className={classes.workspaceTitle}
                        >
                          My workspace
                        </h2>
                      </div>
                      <MyWorkspace />
                    </section>
                    {/* Only while the reader is in no team: the one hint the
                        page gives on that state, in place of the "team not
                        set" caption and the ownership card (DX-546). */}
                    <TeamNotSet />
                  </div>
                  <div className={classes.reviews}>
                    <div
                      id={PR_WIDGET_ANCHOR_ID}
                      style={{ scrollMarginTop: 16 }}
                    >
                      <PullRequestsCard />
                    </div>
                  </div>
                  <div className={classes.recent}>
                    <RecentlyUsed />
                  </div>
                </div>
              </Grid>

              {/*
                The detail the rows above lead into, in the order the page used
                to open on (specs/home-page.md AC-3): the components the
                reader's team owns, what was deployed lately, then CI health
                across the organisations.

                A fixed arrangement rather than the draggable grid. Its edit
                mode saves to localStorage, stops a reader who saved one from
                receiving widgets added later, and offers "Clear all" with no
                way back once the page is empty. DX-138 decided the arrangement
                should persist per user; this waits for its conditions.
              */}
              <Grid item xs={12}>
                <ServicesYouOwn />
              </Grid>
              <Grid item xs={12}>
                <RecentDeployments />
              </Grid>
              <Grid item xs={12}>
                <div id={CI_WIDGET_ANCHOR_ID} style={{ scrollMarginTop: 16 }}>
                  <CiHealthOverview />
                </div>
              </Grid>

              {/*
                The four fields, as links rather than cards. They were tiles at
                the top and are rows in the navigation now, but the page a reader
                lands on should still show that the areas exist.
              */}
              <Grid item xs={12}>
                <Box className={classes.entryStrip}>
                  <span className={classes.entryStripLabel}>Areas</span>
                  {fieldLinks.map(field => (
                    <Link
                      key={field.label}
                      to={field.to}
                      className={classes.entryStripLink}
                    >
                      {field.label}
                    </Link>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </PullRequestListsProvider>
        </HomeSourcesProvider>
      </Content>
    </Page>
  );
};
